import { getParams } from './URLUtils.js';
import { QG_CLIENTS, QI_CLIENTS, FAETHM_CLIENTS, PCAS_CLIENTS, BOB_CLIENTS, GED_CLIENTS } from './themeConfig.js';

export function getThemeForClientId (clientIdRaw) {
    const clientId = (clientIdRaw || '').toString();
    if (CLIENT.REVIBE === clientId) return 'REVIBE';
    if (FAETHM_CLIENTS.some(c => clientId.includes(c))) return 'FAETHM';
    if (BOB_CLIENTS.some(c => clientId.includes(c))) return 'FAETHM';
    if (QI_CLIENTS.some(c => clientId.includes(c))) return 'QI';
    if (QG_CLIENTS.some(c => clientId.includes(c))) return 'QG';
    if (GED_CLIENTS.some(c => clientId.includes(c))) return 'GED';
    if (PCAS_CLIENTS.some(c => clientId.includes(c))) return 'PCAS';
    return 'BASE';
}

const CLIENT_MAP = {
    HK_LONGMAN: ['ilongman.com', 'https://register.pearson.com.hk/'],
    ED_EXCEL_GATEWAY: 'edexcelgateway.com',
    CERT_PREP: ['teacherpd-staging.pearson.com', 'https://teacherpd.pearson.com'],
    UK_STACK: 'ukgsc',
    KIBO: 'msp-sso.azurewebsites.net',
    SKILLING_PARTNER: ['pearson-backend.career-shaper.com', 'dev.spp.pvue2.com', 'prod-back.spp.pvue2.com', 'prod.spp.pvue2.com'],
    FAETHM: 'faethm',
    BOB_CLIENT: ['urn:amazon:cognito:sp:eu-west-2_XZu0X6Su0', 'urn:amazon:cognito:sp:eu-west-2_ueF9h61zD'],
    DAVIDE: ['https://pearson.tst.macopedia-dev.pl', 'https://www.pearson.com/languages/educators/account'],
    PSC_CLIENT: 'PSC-Client',
    MHBHPI_CLIENT: 'MHBHPI-Client',
    MHBBDC_CLIENT: 'MHBBDC-Client'
};

const REGISTRATION_CLIENT_MAP = {
    'ilongman.com': 'HK_LONGMAN',
    'register.pearson.com.hk': 'HK_LONGMAN',
    'edexcelgateway.com': 'ED_EXCEL_GATEWAY',
    'teacherpd-staging.pearson.com': 'CERT_PREP',
    'teacherpd.pearson.com': 'CERT_PREP',
    'skilling-staging.pearsonvue.com': 'CERT_PREP',
    'skilling.pearsonvue.com': 'CERT_PREP',
    'msp-sso.azurewebsites.net': 'KIBO',
    'pearson-backend.career-shaper.com': 'SKILLING_PARTNER',
    'dev.spp.pvue2.com': 'SKILLING_PARTNER',
    'prod-back.spp.pvue2.com': 'SKILLING_PARTNER',
    'prod.spp.pvue2.com': 'SKILLING_PARTNER',
    'uat.mct.pvue2.com': 'MCT',
    'PAICE-V2': 'PAICE',
    'credly': 'CREDLY',
    'PVS-Client': 'PVS',
    'pearsonvirtualschools': 'PVS',
    'admin-dashboard.pearsoned.com': 'EAD',
    'admin-dashboard-stg.pearsoned.com': 'EAD',
    'pearsonassessments': 'PMC',
    'pearsonschoolsandfecolleges': 'PMC',
    'uat.pearsonschoolsandfecolleges.co.uk': 'PMC',
    'teacherhub-dev.pearsondev.tech': 'TEACHER_HUB',
    'teacherhub': 'TEACHER_HUB',
    'MHBHPI-Client': 'HPI',
    'learninghub.uat.pqs-nonprod.pearsondev.tech': 'LEARNING_HUB',
    'learninghub': 'LEARNING_HUB',
    'getrevibe': 'REVIBE',
    'centreconfirmation.uat.pqs-nonprod': 'UK_QUALS',
    'centreconfirmation': 'UK_QUALS',
    'PSC-Client': 'PMC',
    'ELLTHUB-Client': 'TEACHER_HUB',
    'REVIBE-Client': 'REVIBE',
    'EAD-Client': 'EAD',
    'PP-Client': 'PEARSON_PROGRESS'
};

const REGISTRATION_CLIENT_KEYS_BY_LENGTH_DESC = Object.keys(REGISTRATION_CLIENT_MAP)
    .sort((a, b) => b.length - a.length);

export function getRegistrationClientByUrlClientId () {
    try {
        const urlClientId = getRawClientIdFromUrl();
        if (!urlClientId) return null;

        const cleanedClientId = urlClientId.includes('#') ? urlClientId.split('#')[0] : urlClientId;
        if (REGISTRATION_CLIENT_MAP[cleanedClientId]) {
            return REGISTRATION_CLIENT_MAP[cleanedClientId];
        }

        const normalizedClientId = cleanedClientId.toLowerCase();
        const matchedKey = REGISTRATION_CLIENT_KEYS_BY_LENGTH_DESC.find(key => normalizedClientId.includes(key.toLowerCase()));
        return matchedKey ? REGISTRATION_CLIENT_MAP[matchedKey] : null;
    } catch (error) {
        console.error('Error in getRegistrationClientByUrlClientId:', error);
        return null;
    }
}

function getRawClientIdFromUrl () {
    // Extract query parameters from the current URL
    const queryParams = getParams();
    const goToParam = queryParams.get('goto');
    const clientIdParam = queryParams.get('clientId') || queryParams.get('client_id');
    const relayStateParam = queryParams.get('RelayState');

    // If there's no 'goto' parameter, return clientId or relayState from direct query params
    if (!goToParam && !window.location.href.includes('goto=')) {
        return clientIdParam || relayStateParam;
    }

    // Handle encoded client_id in URL (e.g., reset password page with encoded parameters)
    if (!goToParam && window.location.href.includes('goto=')) {
        const regexClientId = window.location.href.match(/client_id%253D([0-9A-Za-z\\-]+)%25/);
        return (regexClientId && regexClientId.length > 1) ? regexClientId[1] : null;
    }

    // If no goto parameter found, return null
    if (!goToParam) return null;

    // Decode the goto parameter and extract nested query parameters
    const decodedGoToParam = decodeURIComponent(goToParam);
    let goToQueryParams = new URLSearchParams(decodedGoToParam.split('?')[1]);
    let params = new URLSearchParams(goToQueryParams);
    let spEntityId = params?.get('spEntityID');
    let relayState = params?.get('RelayState');
    let clientIdInGoToUrl = params?.get('clientId') || params?.get('client_id');

    // If initial parameters weren't found, try parsing from the second query string split
    if (!clientIdInGoToUrl && !spEntityId && !relayState) {
        goToQueryParams = new URLSearchParams(decodedGoToParam.split('?')[2]);
        params = new URLSearchParams(goToQueryParams);
        spEntityId = params?.get('spEntityID');
        relayState = params?.get('RelayState');
        clientIdInGoToUrl = params?.get('clientId') || params?.get('client_id');
    }

    // Extract hostname from the decoded goto URL
    let sppClientId = null;
    try {
        sppClientId = (new URL(decodedGoToParam, window.location.origin)).hostname;
    } catch (e) {
        sppClientId = null;
    }
    // Return the first found client identifier in priority order
    return spEntityId || relayState || clientIdInGoToUrl || clientIdParam || relayStateParam || sppClientId;
}

export const CLIENT = {
    HK_LONGMAN: 'HK_LONGMAN',
    ED_EXCEL_GATEWAY: 'https://www.edexcelgateway.com',
    CERT_PREP: 'CERT_PREP',
    UK_STACK: 'UK_STACK',
    KIBO: 'KIBO',
    SKILLING_PARTNER: 'SPP_CLIENT',
    REVIBE: 'REVIBE-Client',
    FAETHM: 'faethm',
    BOB_CLIENT: 'BOB_Client',
    PVS_SCHOLARSHIP: 'PVSCHP-Client',
    POA_CLIENT: 'POA-Client',
    DAVIDE: 'DAVIDE-Client',
    PSC_CLIENT: 'PSC-Client',
    MHBHPI_CLIENT: 'MHBHPI-Client',
    MHBBDC_CLIENT: 'MHBBDC-Client'
};

export const UPC_CLIENTS = [
    CLIENT.HK_LONGMAN,
    CLIENT.SKILLING_PARTNER,
    CLIENT.CERT_PREP,
    CLIENT.DAVIDE
];

export const MINIMAL_ATTRIBUTE_CLIENT_MAP = {
    'pte_ignite_oauth_client_dev': 'PTE',
    'pte_ignite_oauth_client_intuat': 'PTE',
    'pte_ignite_oauth_client_stg': 'PTE',
    'pte_ignite_oauth_client': 'PTE',
    'pte_ignite_oauth_client_ppe': 'PTE',
    'DAVIDE-Client': 'DAVIDE'
};

export function getMinimalAttributeClient (clientId) {
    return MINIMAL_ATTRIBUTE_CLIENT_MAP[clientId] || null;
}

export function isUPCEnabled (clientId) {
    // Special check for clinical Europe clients
    if (clientId?.includes('clinical-europe')) {
        return true;
    }

    // Check against the list of enabled clients
    return UPC_CLIENTS.includes(clientId);
}

export function getClientById () {
    try {
        return getClientByIdRaw();
    } catch (error) {
        console.error('Error in getClientById:', error);
        return null;
    }
}

/**
 * Prefer locale-specific clinical-europe-* ID over generic clinicaleu-client from query params.
 */
function isGenericClinicalEuropeClientId (clientId) {
    return clientId?.toLowerCase().includes('clinicaleu-client');
}

export function resolveRegistrationClientId (queryClientId) {
    const resolved = getClientById();
    if (isGenericClinicalEuropeClientId(queryClientId) && resolved?.startsWith('clinical-europe-')) {
        return resolved;
    }
    return queryClientId || resolved;
}

function getClientByIdRaw () {
    // special case QGlobal client
    if (window.location.href.includes('QGlobal-Client')) {
        return 'QGlobal-Client';
    } else if (window.location.href.includes('QGlobalCA-Client')) {
        return 'QGlobalCA-Client';
    } else if (window.location.href.includes('ged-client')) {
        return 'ged-client';
    } else if (window.location.href.includes('ged-student-client')) {
        return 'ged-student-client';
    }
    const queryParams = getParams();
    const goToParam = queryParams.get('goto');
    const clientIdParam = queryParams.get('clientId') || queryParams.get('client_id');
    const relayStateParam = queryParams.get('RelayState');

    if (!goToParam && !window.location.href.includes('goto=')) {
        // maybe we have client id or relay state in querystring param?
        return clientIdParam || relayStateParam;
    } else if (!goToParam && window.location.href.includes('goto=')) {
        // we might in resetpassword page
        const regexClientId = window.location.href.match(/client_id%253D([0-9A-Za-z\\-]+)%25/);
        return (regexClientId && regexClientId.length > 1) ? regexClientId[1] : null;
    }

    const decodedGoToParam = decodeURIComponent(queryParams.get('goto'));
    const sppClientId = (new URL(decodedGoToParam)).hostname;
    let goToQueryParams = new URLSearchParams(decodedGoToParam.split('?')[1]);
    let params = new URLSearchParams(goToQueryParams);
    let spEntityId = params?.get('spEntityID');
    let relayState = params?.get('RelayState');
    let realmId = params?.get('realm')?.replace(/[^a-zA-Z0-9]/g, '') || null;
    let clientIdInGoToUrl = params?.get('clientId') || params?.get('client_id');

    if (!clientIdInGoToUrl && !spEntityId && !relayState) {
        goToQueryParams = new URLSearchParams(decodedGoToParam.split('?')[2]);
        params = new URLSearchParams(goToQueryParams);
        spEntityId = params?.get('spEntityID');
        relayState = params?.get('RelayState');
        clientIdInGoToUrl = params?.get('clientId') || params?.get('client_id');
    }

    if (spEntityId?.includes(CLIENT_MAP.HK_LONGMAN[0]) || spEntityId?.includes(CLIENT_MAP.HK_LONGMAN[1]) ||
        relayState?.includes(CLIENT_MAP.HK_LONGMAN[0]) || relayState?.includes(CLIENT_MAP.HK_LONGMAN[1]) ||
        clientIdInGoToUrl?.includes(CLIENT_MAP.HK_LONGMAN[0]) || clientIdInGoToUrl?.includes(CLIENT_MAP.HK_LONGMAN[1])) {
        return CLIENT.HK_LONGMAN;
    }

    if (spEntityId?.includes(CLIENT_MAP.ED_EXCEL_GATEWAY) || relayState?.includes(CLIENT_MAP.ED_EXCEL_GATEWAY) || clientIdInGoToUrl?.includes(CLIENT_MAP.ED_EXCEL_GATEWAY)) {
        return CLIENT.ED_EXCEL_GATEWAY;
    }

    if (spEntityId?.includes(CLIENT_MAP.CERT_PREP[0]) || relayState?.includes(CLIENT_MAP.CERT_PREP[0]) || clientIdInGoToUrl?.includes(CLIENT_MAP.CERT_PREP[0]) ||
        spEntityId?.includes(CLIENT_MAP.CERT_PREP[1]) || relayState?.includes(CLIENT_MAP.CERT_PREP[1]) || clientIdInGoToUrl?.includes(CLIENT_MAP.CERT_PREP[1])) {
        return CLIENT.CERT_PREP;
    }
    if (spEntityId?.includes(CLIENT_MAP.BOB_CLIENT[0]) || relayState?.includes(CLIENT_MAP.BOB_CLIENT[0]) || clientIdInGoToUrl?.includes(CLIENT_MAP.BOB_CLIENT[0]) ||
        spEntityId?.includes(CLIENT_MAP.BOB_CLIENT[1]) || relayState?.includes(CLIENT_MAP.BOB_CLIENT[1]) || clientIdInGoToUrl?.includes(CLIENT_MAP.BOB_CLIENT[1])) {
        return CLIENT.BOB_CLIENT;
    }

    // special handling for clinical europe
    if (spEntityId?.toLowerCase().includes('clinicaleu-client') || relayState?.toLowerCase().includes('clinicaleu-client') || clientIdInGoToUrl?.toLowerCase().includes('clinicaleu-client')) {
        const redirecturl = queryParams.get('redirect_uri') || params?.get('redirect_uri');
        if (!redirecturl) {
            console.log('no redirect_uri found for clinical europe client.');
        } else {
            try {
                const hostname = new URL(redirecturl).hostname.toLowerCase();
                // here is the trick -- in dev we have fr-dev.ecta.pearsondev.tech, but in prod we have www.pearsonclinical.fr
                for (const locale of ['nl', 'de', 'fr', 'se', 'no', 'da', 'es', 'dk']) {
                    if (hostname.includes(`${locale}-`) || hostname.includes(`.${locale}`)) {
                        const clientLocale = locale === 'dk' ? 'da' : locale;
                        return `clinical-europe-${clientLocale}`;
                    }
                }

                console.log(`hostname: ${hostname} no locale found.`);
            } catch (e) {
                console.log('error while parsing redirect_uri', e);
            }
        }
    }
    if (realmId?.includes(CLIENT_MAP.UK_STACK)) {
        return CLIENT.UK_STACK;
    }

    if (spEntityId?.includes(CLIENT_MAP.KIBO) || relayState?.includes(CLIENT_MAP.KIBO) || clientIdInGoToUrl?.includes(CLIENT_MAP.KIBO)) {
        return CLIENT.KIBO;
    }
    if (spEntityId?.includes(CLIENT_MAP.FAETHM) || relayState?.includes(CLIENT_MAP.FAETHM) || clientIdInGoToUrl?.includes(CLIENT_MAP.FAETHM)) {
        return CLIENT.FAETHM;
    }

    if (sppClientId?.includes(CLIENT_MAP.SKILLING_PARTNER[0]) || sppClientId?.includes(CLIENT_MAP.SKILLING_PARTNER[1]) || sppClientId?.includes(CLIENT_MAP.SKILLING_PARTNER[2]) || sppClientId?.includes(CLIENT_MAP.SKILLING_PARTNER[3])) {
        return CLIENT.SKILLING_PARTNER;
    }

    if (spEntityId?.includes(CLIENT_MAP.DAVIDE[0]) || relayState?.includes(CLIENT_MAP.DAVIDE[0]) || clientIdInGoToUrl?.includes(CLIENT_MAP.DAVIDE[0]) || clientIdParam?.includes(CLIENT_MAP.DAVIDE[0]) ||
        spEntityId?.includes(CLIENT_MAP.DAVIDE[1]) || relayState?.includes(CLIENT_MAP.DAVIDE[1]) || clientIdInGoToUrl?.includes(CLIENT_MAP.DAVIDE[1]) || clientIdParam?.includes(CLIENT_MAP.DAVIDE[1])) {
        return CLIENT.DAVIDE;
    }

    if (spEntityId?.includes(CLIENT_MAP.PSC_CLIENT) || relayState?.includes(CLIENT_MAP.PSC_CLIENT) || clientIdInGoToUrl?.includes(CLIENT_MAP.PSC_CLIENT) || clientIdParam?.includes(CLIENT_MAP.PSC_CLIENT)) {
        return CLIENT.PSC_CLIENT;
    }

    if (spEntityId?.includes(CLIENT_MAP.MHBHPI_CLIENT) || relayState?.includes(CLIENT_MAP.MHBHPI_CLIENT) || clientIdInGoToUrl?.includes(CLIENT_MAP.MHBHPI_CLIENT) || clientIdParam?.includes(CLIENT_MAP.MHBHPI_CLIENT)) {
        return CLIENT.MHBHPI_CLIENT;
    }

    if (spEntityId?.includes(CLIENT_MAP.MHBBDC_CLIENT) || relayState?.includes(CLIENT_MAP.MHBBDC_CLIENT) || clientIdInGoToUrl?.includes(CLIENT_MAP.MHBBDC_CLIENT) || clientIdParam?.includes(CLIENT_MAP.MHBBDC_CLIENT)) {
        return CLIENT.MHBBDC_CLIENT;
    }

    // Get the client ID and clean up any hash fragments
    let finalClientId = spEntityId || relayState || clientIdInGoToUrl || clientIdParam || relayStateParam || sppClientId;

    // Remove any hash fragments from the client ID
    if (finalClientId && finalClientId.includes('#')) {
        finalClientId = finalClientId.split('#')[0];
    }

    return finalClientId; // fallback
}
